import { type HelpArticle, type Role, meetsRole } from "../types";
import { gettingStartedArticles } from "./gettingStarted";
import { operationsArticles } from "./operations";
import { managementArticles } from "./management";
import { adminArticles } from "./admin";

export const allArticles: HelpArticle[] = [
  ...gettingStartedArticles,
  ...operationsArticles,
  ...managementArticles,
  ...adminArticles,
];

/** Articles the given role is allowed to see. */
export function visibleArticles(role: Role | null | undefined): HelpArticle[] {
  return allArticles.filter((a) => meetsRole(role, a.minRole));
}

export function getArticle(id: string): HelpArticle | undefined {
  return allArticles.find((a) => a.id === id);
}

/** How well `prefix` matches `pathname`; -1 for no match, longer = more specific. */
function matchLen(pathname: string, prefix: string): number {
  if (prefix === "/") return pathname === "/" ? 1 : -1;
  if (pathname === prefix || pathname.startsWith(prefix + "/")) return prefix.length;
  return -1;
}

/** The article that is contextual help for a given route (longest prefix wins). */
export function articleForPath(pathname: string, role?: Role | null): HelpArticle | undefined {
  let best: HelpArticle | undefined;
  let bestLen = -1;
  for (const article of visibleArticles(role)) {
    for (const prefix of article.routeMatch ?? []) {
      const len = matchLen(pathname, prefix);
      if (len > bestLen) {
        bestLen = len;
        best = article;
      }
    }
  }
  return best;
}

function haystacks(a: HelpArticle): { text: string; weight: number }[] {
  const conceptText = (a.concepts ?? []).map((c) => `${c.term} ${c.definition}`).join(" ");
  const taskText = (a.tasks ?? []).map((t) => `${t.title} ${t.intro ?? ""} ${t.steps.map((s) => s.text).join(" ")}`).join(" ");
  const faqText = (a.faqs ?? []).map((f) => `${f.q} ${f.a}`).join(" ");
  const troubleText = (a.troubleshooting ?? []).map((t) => `${t.problem} ${t.fix}`).join(" ");
  return [
    { text: a.title, weight: 10 },
    { text: (a.keywords ?? []).join(" "), weight: 6 },
    { text: a.summary, weight: 5 },
    { text: (a.concepts ?? []).map((c) => c.term).join(" "), weight: 5 },
    { text: (a.tasks ?? []).map((t) => t.title).join(" "), weight: 4 },
    { text: a.overview, weight: 1 },
    { text: conceptText, weight: 1 },
    { text: taskText, weight: 1 },
    { text: faqText, weight: 1 },
    { text: troubleText, weight: 1 },
  ];
}

function scoreArticle(a: HelpArticle, tokens: string[]): number {
  const fields = haystacks(a).map((h) => ({ text: h.text.toLowerCase(), weight: h.weight }));
  let score = 0;
  for (const token of tokens) {
    let tokenScore = 0;
    for (const f of fields) {
      if (f.text.includes(token)) tokenScore = Math.max(tokenScore, f.weight);
    }
    if (tokenScore === 0) return 0; // every token must match somewhere
    score += tokenScore;
  }
  return score;
}

/** Full-text search across visible articles, ranked by relevance. */
export function searchArticles(query: string, role?: Role | null): HelpArticle[] {
  const q = query.trim().toLowerCase();
  const visible = visibleArticles(role);
  if (!q) return visible;
  const tokens = q.split(/\s+/).filter(Boolean);
  return visible
    .map((article) => ({ article, score: scoreArticle(article, tokens) }))
    .filter((r) => r.score > 0)
    .sort((x, y) => y.score - x.score)
    .map((r) => r.article);
}
